import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import axios from 'axios';
import { convertFromRaw, EditorState, Modifier, SelectionState } from 'draft-js';
import getDefaultKeyBinding from 'draft-js/lib/getDefaultKeyBinding';
import * as draftJSUtils from 'draftjs-utils';
import { isEmpty, isNil, head, when, last, difference, map, pipe } from 'ramda';
import { useDispatch, useSelector } from 'react-redux';

import {
  PAGE_STATUS_APPROVED,
  PAGE_TYPE_ABOUT_SAFARI,
  PAGE_TYPE_ABOUT_US,
  PAGE_TYPE_ACTIVITY,
  PAGE_TYPE_AREA,
  PAGE_TYPE_HELP_ARTICLE,
  PAGE_TYPE_HELP_TUTORIAL,
  PAGE_TYPE_MISC,
  PAGE_TYPE_PRE_TRAVEL_INFO,
  PAGE_TYPE_PROPERTY,
} from 'constants/index';
import { useSnackbar } from 'hooks/useSnackbar';
import { searchPagesThunk } from 'store/itineraryDetails/thunks';
import { formatWysiwygText } from 'utils/common';
import { debounce } from 'utils/debounce';
import { fork, getIn, isDefined } from 'utils/ramda';

import { accountIdSelector } from '../../../../../../store/user/selectors';
import { KEY_BINDING_MAP, PAGE_MENTION_ENTITY_TYPE, MENTION_TRIGGER, EDITOR_TYPES } from './constants';
import { getEntities } from './functions';

const detailsSuggestionsTypesToSearch = [
  PAGE_TYPE_AREA,
  PAGE_TYPE_ACTIVITY,
  PAGE_TYPE_MISC,
  PAGE_TYPE_ABOUT_SAFARI,
  PAGE_TYPE_ABOUT_US,
  PAGE_TYPE_PRE_TRAVEL_INFO,
  PAGE_TYPE_HELP_ARTICLE,
  PAGE_TYPE_HELP_TUTORIAL,
  PAGE_TYPE_PROPERTY,
];

const EDITOR_TYPE_TO_SUGGESTIONS_TYPES = {
  [EDITOR_TYPES.DETAILS]: detailsSuggestionsTypesToSearch,
  [EDITOR_TYPES.ACCOMMODATION]: [PAGE_TYPE_PROPERTY],
};

export const EditorContext = createContext();
export const useEditorContext = () => useContext(EditorContext);

const editingBlockInitialState = {
  isOpen: false,
  blockType: null,
  blockKey: null,
  blockRect: null,
  blockEl: null,
  blockStart: null,
  blockEnd: null,
  data: {},
};

export const EDITING_BLOCK_TYPES = {
  SUGGESTIONS: 'SUGGESTIONS',
  ENTITY_EDITOR: 'ENTITY_EDITOR',
  AREA_SELECT: 'AREA_SELECT',
  CREATE_NEW_PAGE: 'CREATE_NEW_PAGE',
  CREATE_NEW_AREA_PAGE: 'CREATE_NEW_AREA_PAGE',
  CREATE_NEW_PROPERTY_PAGE: 'CREATE_NEW_PROPERTY_PAGE',
};

const buildEntityName = (entityMeta = {}) => {
  const { handle = '', rooms = [], area = {} } = entityMeta;
  const namePart = `${MENTION_TRIGGER}${handle}`;
  const roomsPart = isEmpty(rooms) ? '' : `${rooms.map((room) => room.name).join(' ')}`;
  const areaPart = area.handle ? `${area.handle}` : '';

  return [namePart, roomsPart, areaPart].filter(Boolean).join(' ');
};

export function EditorContextProvider(props) {
  const onSnackbar = useSnackbar();
  const dispatch = useDispatch();
  const {
    decorators,
    initialRawContentState,
    pages,
    children,
    onContentStateChange,
    type: editorType,
    editor: editorEl,
    mountEl,
    onRemoveMention,
    additionalFilterQueryParams = {},
  } = props;
  const [editorState, setEditorState] = useState(() =>
    !isNil(initialRawContentState) && !isEmpty(initialRawContentState)
      ? EditorState.createWithContent(convertFromRaw(initialRawContentState), decorators)
      : EditorState.createEmpty(decorators),
  );

  const handleSetEditorState = (newState) => {
    if (onContentStateChange) {
      const currentContentState = editorState.getCurrentContent();
      const currentContent = newState.getCurrentContent();

      const handleRemoveEntities = (entities) => setTimeout(() => map(handleRemoveEntity, entities), 0);

      const removeListener = fork(
        pipe(difference, when(isDefined, handleRemoveEntities)),
        pipe(head, getEntities, map(getIn('entityKey'))),
        pipe(last, getEntities, map(getIn('entityKey'))),
      );

      removeListener([editorState, newState]);

      if ((currentContentState.hasText() || currentContent.hasText()) && currentContentState !== currentContent) {
        onContentStateChange(trimEditorState(newState).getCurrentContent());
      }
    }

    setEditorState(newState);
  };
  const [editingBlockState, setEditingBlockState] = useState(editingBlockInitialState);
  const resetEditingBlockState = () => {
    setEditingBlockState(editingBlockInitialState);
  };

  const focusEditor = () => {
    if (editorEl) editorEl.focus();
  };
  const blurEditor = () => {
    if (editorEl) editorEl.blur();
  };

  const accountId = useSelector(accountIdSelector);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const fetchSuggestionsDebounced = useCallback(
    debounce(async (q, additionalFilterQueryParams) => {
      setIsLoadingSuggestions(true);

      try {
        const pages = await dispatch(
          searchPagesThunk({
            limit: 100,
            q,
            account_id: accountId,
            ...additionalFilterQueryParams,
            types: EDITOR_TYPE_TO_SUGGESTIONS_TYPES[editorType],
            statuses: [PAGE_STATUS_APPROVED],
            orderBy: 'handle',
            countless: '1',
          }),
        );
        setSuggestions(pages);
        setIsLoadingSuggestions(false);
      } catch (err) {
        setIsLoadingSuggestions(false);

        if (!(err instanceof axios.Cancel)) {
          onSnackbar("Couldn't fetch pages.", true);
          throw err;
        }
      }
    }, 1000),
    [],
  );
  useEffect(() => {
    if (!editingBlockState.isOpen || editingBlockState.blockType !== EDITING_BLOCK_TYPES.SUGGESTIONS) {
      setSuggestions([]);
      return;
    }

    if (!isNil(editingBlockState.data?.query))
      fetchSuggestionsDebounced(editingBlockState.data.query, additionalFilterQueryParams);
  }, [
    additionalFilterQueryParams,
    editingBlockState.isOpen,
    editingBlockState.blockType,
    editingBlockState.data?.query,
  ]);

  const handleCreatePageEntity = (page) => {
    const meta = { id: page.id, type: page.type, name: page.name, handle: page.handle };
    const newState = createAndReplaceEntity(editingBlockState, meta, editorState);
    handleSetEditorState(newState);
    return newState;
  };
  const handlePickSuggestion = (suggestion) => {
    const newState = handleCreatePageEntity(suggestion);
    if (editorType === EDITOR_TYPES.ACCOMMODATION) {
      const entityKey = newState.getCurrentContent().getLastCreatedEntityKey();
      const entityRange = draftJSUtils.getEntityRange(newState, entityKey);

      // setTimeout to wait that entity is in the DOM
      setTimeout(() => {
        const entityElement = document.getElementById(entityKey);
        const entityRect = entityElement?.getBoundingClientRect() ?? editingBlockState.blockRect;
        setEditingBlockState({
          isOpen: true,
          blockType: EDITING_BLOCK_TYPES.ENTITY_EDITOR,
          blockKey: entityKey,
          blockRect: getElementPosition(mountEl, entityRect),
          blockEl: entityElement,
          blockStart: entityRange.start,
          blockEnd: entityRange.end,
        });
      }, 0);
    } else {
      resetEditingBlockState();
    }
  };
  const handleUpdateEntity = (data) => {
    const currentMeta = editorState.getCurrentContent().getEntity(editingBlockState.blockKey).getData();
    const newMeta = { ...currentMeta, ...data };
    const newState = createAndReplaceEntity(editingBlockState, newMeta, editorState, { replaceWholeBlock: true });
    const lastEntityKey = newState.getCurrentContent().getLastCreatedEntityKey();
    const entityRange = draftJSUtils.getEntityRange(newState, lastEntityKey);
    handleSetEditorState(newState);

    setEditingBlockState((prevState) => ({
      ...prevState,
      blockKey: lastEntityKey,
      blockStart: entityRange.start,
      blockEnd: entityRange.end,
    }));
  };
  const handleAddRoomsToEntity = (rooms) => {
    handleUpdateEntity({ rooms });
  };
  const handleAddAreaToEntity = (area) => {
    handleUpdateEntity({ area });
  };
  const handleRemoveAreaFromEntity = () => {
    handleUpdateEntity({ area: undefined });
  };
  const handleOpenMyOwnAreaSelect = (data) => {
    setEditingBlockState((prevState) => ({
      ...prevState,
      isOpen: true,
      blockType: EDITING_BLOCK_TYPES.AREA_SELECT,
      data,
    }));
  };
  const handleOpenCreateNewPage = () => {
    setEditingBlockState((prevState) => {
      let blockType = EDITING_BLOCK_TYPES.CREATE_NEW_PAGE;
      if (editorType === EDITOR_TYPES.ACCOMMODATION) {
        blockType =
          prevState.blockType === EDITING_BLOCK_TYPES.AREA_SELECT
            ? EDITING_BLOCK_TYPES.CREATE_NEW_AREA_PAGE
            : EDITING_BLOCK_TYPES.CREATE_NEW_PROPERTY_PAGE;
      }

      return {
        ...prevState,
        isOpen: true,
        blockType,
      };
    });
  };
  const handleCreateNewPage = (blockType, newPage) => {
    if (blockType === EDITING_BLOCK_TYPES.CREATE_NEW_PAGE) {
      handleCreatePageEntity(newPage);
      resetEditingBlockState();
      return;
    } else if (blockType === EDITING_BLOCK_TYPES.CREATE_NEW_PROPERTY_PAGE) {
      const newState = handleCreatePageEntity(newPage);
      const entityKey = newState.getCurrentContent().getLastCreatedEntityKey();
      const entityRange = draftJSUtils.getEntityRange(newState, entityKey);

      // setTimeout to wait that entity is in the DOM
      setTimeout(() => {
        const entityElement = document.getElementById(entityKey);
        const entityRect = entityElement?.getBoundingClientRect() ?? editingBlockState.blockRect;
        setEditingBlockState({
          isOpen: true,
          blockType: EDITING_BLOCK_TYPES.ENTITY_EDITOR,
          blockKey: entityKey,
          blockRect: getElementPosition(mountEl, entityRect),
          blockEl: entityElement,
          blockStart: entityRange.start,
          blockEnd: entityRange.end,
        });
      }, 0);
      return;
    } else if (blockType === EDITING_BLOCK_TYPES.CREATE_NEW_AREA_PAGE) {
      handleAddAreaToEntity(newPage);
      resetEditingBlockState();
      return;
    }
  };

  const [focusedSuggestionIdx, setFocusedSuggestion] = useState(null);
  useEffect(() => {
    if (!isNil(focusedSuggestionIdx) && isNil(suggestions[focusedSuggestionIdx])) {
      setFocusedSuggestion(null);
    } else if (isNil(focusedSuggestionIdx) && suggestions && !isEmpty(suggestions)) {
      setFocusedSuggestion(0);
    }
  }, [focusedSuggestionIdx, suggestions]);
  const keyBindingFn = (event) => {
    if (!editingBlockState.isOpen) {
      return getDefaultKeyBinding(event);
    }

    if (event.keyCode === 38) {
      return KEY_BINDING_MAP.ARROW_UP;
    } else if (event.keyCode === 40) {
      return KEY_BINDING_MAP.ARROW_DOWN;
    } else if (event.keyCode === 13 && !isNil(focusedSuggestionIdx)) {
      return KEY_BINDING_MAP.ENTER;
    } else if (event.keyCode === 27) {
      return KEY_BINDING_MAP.ESC;
    }

    return getDefaultKeyBinding(event);
  };
  const handleKeyCommand = (command) => {
    switch (command) {
      case KEY_BINDING_MAP.ENTER: {
        handlePickSuggestion(suggestions[focusedSuggestionIdx]);
        return 'handled';
      }

      case KEY_BINDING_MAP.ESC: {
        resetEditingBlockState();
        return 'handled';
      }

      case KEY_BINDING_MAP.ARROW_DOWN: {
        const nextIdx = isNil(focusedSuggestionIdx) ? 0 : (focusedSuggestionIdx + 1) % suggestions.length;
        setFocusedSuggestion(nextIdx);
        return 'handled';
      }
      case KEY_BINDING_MAP.ARROW_UP: {
        setFocusedSuggestion((focusedSuggestionIdx + suggestions.length - 1) % suggestions.length);
        return 'handled';
      }
      default:
        return 'not-handled';
    }
  };

  const handleRemoveEntity = (entityKey) => {
    const meta = editorState.getCurrentContent().getEntity(entityKey).getData();
    if (onRemoveMention) onRemoveMention(meta);
    setEditingBlockState((prevState) => {
      if (prevState.blockKey === entityKey) {
        return editingBlockInitialState;
      }
      return prevState;
    });
  };

  const handlePastedText = (pastedText) => {
    const formattedText = formatWysiwygText(pastedText);

    if (isNil(pastedText)) {
      return 'not-handled';
    }
    const currentContent = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    const newContentState = Modifier.replaceText(currentContent, selection, formattedText);
    handleSetEditorState(EditorState.push(editorState, newContentState, 'insert-fragment'));
    return 'handled';
  };

  return (
    <EditorContext.Provider
      value={{
        editorProps: {
          handleKeyCommand,
          handlePastedText,
          keyBindingFn,
        },
        mountEl,
        editorState,
        setEditorState: handleSetEditorState,
        editingBlockState,
        setEditingBlockState,
        resetEditingBlockState,
        suggestions,
        isLoadingSuggestions,
        handlePickSuggestion,
        focusedSuggestionIdx,
        handleAddRoomsToEntity,
        handleAddAreaToEntity,
        handleRemoveAreaFromEntity,
        handleOpenMyOwnAreaSelect,
        focusEditor,
        blurEditor,
        handleOpenCreateNewPage,
        handleCreateNewPage,
        handleRemoveEntity,
        pages,
      }}
    >
      {children}
    </EditorContext.Provider>
  );
}

export const useMentionDecorator = (props, mentionEl) => {
  const { editorState, editingBlockState, setEditingBlockState, mountEl } = useEditorContext();
  const { decoratedText, offsetKey, blockKey, start, end } = props;
  const selectionState = editorState.getSelection();
  const hasFocus = editorState.getSelection().getHasFocus();
  // const query = decoratedText.trim().slice(MENTION_TRIGGER.length);
  const query = decoratedText.trim().slice(MENTION_TRIGGER.length, selectionState.focusOffset - start);
  const rect = mentionEl?.getBoundingClientRect();

  const shouldOpenEditingBlock = () => {
    const hasEdgeWithin = selectionState.hasEdgeWithin(blockKey, start + MENTION_TRIGGER.length, end);
    return Boolean(rect) && hasEdgeWithin;
  };

  const handleOpenEditingBlock = () => {
    const windowSelection = window.getSelection && window.getSelection();
    const cursorRect =
      windowSelection &&
      windowSelection.focusNode?.parentElement &&
      mentionEl.contains(windowSelection.focusNode.parentElement) &&
      windowSelection.rangeCount > 0
        ? window.getSelection()?.getRangeAt(0)?.getBoundingClientRect()
        : null;
    const isEmptyCursorRect = !cursorRect?.left && !cursorRect?.top;

    setEditingBlockState({
      isOpen: true,
      blockKey: offsetKey,
      blockStart: start,
      blockEnd: end,
      blockRect: getElementPosition(mountEl, isEmptyCursorRect ? rect : cursorRect),
      blockEl: mentionEl,
      blockType: EDITING_BLOCK_TYPES.SUGGESTIONS,
      data: { query },
    });
  };

  useEffect(() => {
    if (!hasFocus) {
      return;
    }

    if (shouldOpenEditingBlock()) {
      handleOpenEditingBlock();
    } else {
      setEditingBlockState((prevState) => {
        if (prevState.blockKey === offsetKey) {
          return editingBlockInitialState;
        }
        return prevState;
      });
    }
  }, [rect?.top, editorState]);

  useEffect(() => {
    return () => {
      setEditingBlockState((prevState) => {
        if (prevState.blockKey === offsetKey) {
          return editingBlockInitialState;
        }
        return prevState;
      });
    };
  }, []);

  useEffect(() => {
    if (editingBlockState.isOpen && editingBlockState.blockKey === offsetKey) {
      setEditingBlockState((prevState) => ({ ...prevState, data: { query } }));
    }
  }, [editingBlockState.isOpen, editingBlockState.blockKey, offsetKey, decoratedText]);
};

export const usePageMentionEntity = (props, mentionEl) => {
  const { entityKey, start, end } = props;
  const { setEditingBlockState, mountEl } = useEditorContext();

  const rect = mentionEl?.getBoundingClientRect();

  const onClick = (e) => {
    e.preventDefault();
    setEditingBlockState({
      isOpen: true,
      blockKey: entityKey,
      blockStart: start,
      blockEnd: end,
      blockRect: getElementPosition(mountEl, rect),
      blockEl: mentionEl,
      blockType: EDITING_BLOCK_TYPES.ENTITY_EDITOR,
    });
  };

  return { onClick };
};

const createAndReplaceEntity = (blockData, meta, editorState, options = { replaceWholeBlock: false }) => {
  const { blockStart, blockEnd } = blockData;
  const contentStateWithEntity = editorState
    .getCurrentContent()
    .createEntity(PAGE_MENTION_ENTITY_TYPE, 'IMMUTABLE', meta);
  const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
  const currentSelectionState = editorState.getSelection();

  // get selection of the @mention search text
  let mentionTextSelection = currentSelectionState.merge({
    anchorOffset: blockStart,
    focusOffset: currentSelectionState.focusOffset,
  });

  const blockKey = mentionTextSelection.getAnchorKey();
  const block = editorState.getCurrentContent().getBlockForKey(blockKey);
  const blockHasWhitespace = last(block.getText().slice(blockStart, currentSelectionState.focusOffset + 1)) === ' ';
  if (blockHasWhitespace) {
    mentionTextSelection = currentSelectionState.merge({
      anchorOffset: blockStart,
      focusOffset: currentSelectionState.focusOffset + 1,
    });
  }

  if (options.replaceWholeBlock) {
    const blockHasWhitespace = last(block.getText().slice(blockStart, blockEnd + 1)) === ' ';

    mentionTextSelection = currentSelectionState.merge({
      anchorOffset: blockStart,
      focusOffset: blockHasWhitespace ? blockEnd + 1 : blockEnd,
    });
  }

  let mentionReplacedContent = Modifier.replaceText(
    editorState.getCurrentContent(),
    mentionTextSelection,
    buildEntityName(meta),
    undefined, // no inline style needed
    entityKey,
  );

  // append a space right after the mention for
  // a smooth writing experience.
  mentionReplacedContent = Modifier.insertText(mentionReplacedContent, mentionReplacedContent.getSelectionAfter(), ' ');

  const newEditorState = EditorState.push(editorState, mentionReplacedContent, 'insert-fragment');
  return EditorState.forceSelection(newEditorState, mentionReplacedContent.getSelectionAfter());
};

const MIN_DISTANCE_WITH_MOUNT_EL = 5;
const normalizeElementPosition = (position, mountEl, element) => {
  const elementRect = element.getBoundingClientRect();
  const xOverflow = position.left + elementRect.width - mountEl.clientWidth + MIN_DISTANCE_WITH_MOUNT_EL;
  const normalizedLeft = xOverflow > 0 ? position.left - xOverflow : position.left;
  return {
    top: position.top,
    left: normalizedLeft,
  };
};
const getElementPosition = (mountEl, anchorRect, shiftY = 5) => {
  const position = {
    top: anchorRect.top + anchorRect.height - mountEl.getBoundingClientRect().top + mountEl.scrollTop + shiftY,
    left: anchorRect.left,
  };

  return position;
};
export const getNormalizedPositionStyles = (position, mountEl, element) => {
  if (!element) {
    return {
      visibility: 'hidden',
      opacity: 0,
    };
  }

  return normalizeElementPosition(position, mountEl, element);
};

const trimEditorState = (currentEditorState) => {
  const currentContent = currentEditorState.getCurrentContent();
  const newContent = currentContent.getBlockMap().reduce((acc, block) => {
    const key = block.getKey();
    const text = block.getText();

    // After each mention we add whitespace
    // however, if this mention is located at the end of the block and the user did not enter anything after
    // we need to remove that whitespace
    // So here we check that specific case: if last symbol of block == whitespace and symbol before it is in the mention
    if (last(text) === ' ' && block.getEntityAt(-2) !== null) {
      return Modifier.replaceText(
        acc,
        new SelectionState({
          anchorKey: key,
          focusKey: key,
          anchorOffset: text.length - 1,
          focusOffset: text.length,
        }),
        '',
      );
    }

    return acc;
  }, currentContent);

  return EditorState.push(currentEditorState, newContent, 'remove-range');
};
